/**
 * The buyer's feedback calls on a branded line sheet (KP-4572):
 * POST /api/v1/public/line-sheets/{token}/feedback/{state|identify|reactions|note}.
 *
 * Every call is a CORS "simple request": POST, a JSON body sent as
 * text/plain, no custom header and no credentials, so the browser sends no
 * preflight. The visitor key and tracked link id travel in the body, never in
 * the URL, so they stay out of proxies and request logs.
 *
 * Nothing here throws. Every outcome is { ok, status, data, retryAfter }:
 * status 0 for a network failure, a timeout or a call that could not be made.
 */

import { publicSheetRoot, withBudget } from './useLineSheetPresentation'

export const FEEDBACK_BUDGET_MS = 4000
export const FEEDBACK_ROUTES = ['state', 'identify', 'reactions', 'note']
const WRITE_ROUTES = ['identify', 'reactions', 'note']

const FAILED = Object.freeze({
  ok: false, status: 0, data: null, retryAfter: 0,
})

/** The feedback route's URL, or null when the base, token or route cannot be used. */
export function feedbackUrl(plgBaseUrl, token, route) {
  const root = publicSheetRoot(plgBaseUrl, token)
  if (!root || !FEEDBACK_ROUTES.includes(route)) return null
  return `${root}/feedback/${route}`
}

async function readJson(res) {
  try {
    return await res.json()
  } catch (e) {
    return null
  }
}

function retryAfterSeconds(res) {
  const raw = res.headers && typeof res.headers.get === 'function' ? res.headers.get('Retry-After') : null
  const n = Number(raw)
  return Number.isFinite(n) && n > 0 ? n : 0
}

async function send(url, init, fetchImpl) {
  const res = await fetchImpl(url, init)
  if (!res) return FAILED
  const data = await readJson(res)
  const ok = Boolean(res.ok) && data !== null
  return {
    ok, status: res.status, data, retryAfter: retryAfterSeconds(res),
  }
}

const defaultFetch = () => (typeof fetch === 'function' ? fetch : null)
const newController = () => (typeof AbortController === 'function' ? new AbortController() : null)

// A CORS simple request: no custom header, no credentials, so no preflight.
function requestInit(body, controller, keepalive) {
  return {
    method: 'POST',
    mode: 'cors',
    credentials: 'omit',
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    body: JSON.stringify(body || {}),
    signal: controller ? controller.signal : undefined,
    ...(keepalive ? { keepalive: true } : {}),
  }
}

/** One feedback call. Never throws. */
export async function postFeedback({
  plgBaseUrl,
  token,
  route,
  body,
  budgetMs = FEEDBACK_BUDGET_MS,
  keepalive = false,
  fetchImpl = defaultFetch(),
}) {
  const url = feedbackUrl(plgBaseUrl, token, route)
  if (!url || !fetchImpl) return FAILED

  const controller = newController()
  try {
    const out = await withBudget(send(url, requestInit(body, controller, keepalive), fetchImpl), controller, budgetMs)
    return out || FAILED
  } catch (e) {
    return FAILED
  }
}

/** The four routes bound to one sheet. Writes ask the browser to finish them if the tab closes. */
export function createFeedbackApi({
  plgBaseUrl, token, fetchImpl, budgetMs,
}) {
  const call = route => body => postFeedback({
    plgBaseUrl,
    token,
    route,
    body,
    budgetMs,
    keepalive: WRITE_ROUTES.includes(route),
    ...(fetchImpl !== undefined ? { fetchImpl } : {}),
  })
  return Object.freeze(Object.fromEntries(FEEDBACK_ROUTES.map(route => [route, call(route)])))
}
